import { createFileRoute } from "@tanstack/react-router";
import { requireAdminRoute } from "@/lib/require-admin-route";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { useTopbar } from "@/components/chrome/use-topbar";

interface AuditRow {
  id: string;
  at: number;
  actorEmail: string;
  group: string;
  field: string;
  before: string | null;
  after: string | null;
  reason: string | null;
}

interface AuditResponse {
  rows: AuditRow[];
}

export const Route = createFileRoute("/_app/settings/audit")({
  beforeLoad: requireAdminRoute,
  component: AuditPage,
});

function formatAt(at: number): string {
  return new Date(at).toISOString().slice(0, 19).replace("T", " ") + " UTC";
}

function AuditPage() {
  const [group, setGroup] = useState("");
  const [actor, setActor] = useState("");
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useQuery<AuditResponse>({
    queryKey: ["settings", "audit", { group, actor, search }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (group) params.set("group", group);
      if (actor) params.set("actor", actor);
      if (search) params.set("search", search);
      const qs = params.toString();
      return apiFetch<AuditResponse>(`/api/settings/audit${qs ? `?${qs}` : ""}`);
    },
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const groups = useMemo(() => Array.from(new Set(rows.map((r) => r.group))).sort(), [rows]);
  const actors = useMemo(() => Array.from(new Set(rows.map((r) => r.actorEmail))).sort(), [rows]);

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "Audit log" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  return (
    <div className="space-y-6">
      <div className="border-b border-rule pb-5">
        <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Audit log</h1>
        <p className="text-sm text-muted">
          Read-only history of every config change. Filter by user, parameter group, or field.
        </p>
      </div>

      <form className="flex flex-wrap gap-2 text-sm" onSubmit={(e) => e.preventDefault()}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search field, reason, before, after…"
          className="flex-1 rounded border border-rule-strong bg-surface px-3 py-2 text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
        />
        <select
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          className="rounded border border-rule-strong bg-surface px-3 py-2 text-ink focus:outline-none focus:ring-1 focus:ring-gold"
        >
          <option value="">All users</option>
          {actors.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          className="rounded border border-rule-strong bg-surface px-3 py-2 text-ink focus:outline-none focus:ring-1 focus:ring-gold"
        >
          <option value="">All parameter groups</option>
          {groups.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
      </form>

      {isLoading ? <p>Loading…</p> : null}
      {error ? <p className="text-red-400">Failed to load audit log.</p> : null}
      {!isLoading && !error ? (
        <div className="overflow-hidden rounded-lg border border-rule bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-paper-2">
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.12em] text-muted">
                  <th className="px-5 py-3">Timestamp</th>
                  <th className="px-5 py-3">User</th>
                  <th className="px-5 py-3">Group</th>
                  <th className="px-5 py-3">Field</th>
                  <th className="px-5 py-3">Before → After</th>
                  <th className="px-5 py-3">Reason / Context</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-rule">
                {rows.map((r) => (
                  <tr key={r.id} className="align-middle hover:bg-paper">
                    <td className="whitespace-nowrap px-5 py-3 text-muted">{formatAt(r.at)}</td>
                    <td className="px-5 py-3 text-ink">{r.actorEmail}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex rounded bg-paper-2 px-2 py-1 text-[11px] font-semibold">
                        {r.group}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">{r.field}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {r.before === null ? (
                          <span className="text-muted-2">—</span>
                        ) : (
                          <span
                            className="max-w-[14rem] truncate rounded border border-rule bg-paper px-2 py-1 text-[11px] text-muted"
                            title={r.before}
                          >
                            {r.before}
                          </span>
                        )}
                        <span className="text-muted-2">→</span>
                        {r.after === null ? (
                          <span className="text-muted-2">—</span>
                        ) : (
                          <span
                            className="max-w-[14rem] truncate rounded border border-pos/20 bg-pos-bg px-2 py-1 text-[11px] font-semibold text-pos"
                            title={r.after}
                          >
                            {r.after}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="max-w-[16rem] px-5 py-3 text-[12px] leading-snug text-muted">
                      {r.reason ?? <span className="text-muted-2">—</span>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-muted">
                      No audit entries yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
